import {
  COUNTRIES,
  LIMITED_COUNTRY_OPTIONS,
  LIMITED_PHONE_COUNTRY_CODE,
  PREFIX_OPTIONS,
  STATES,
} from '../../../constants';
import { Address, PhoneNumber, Provider, ProviderAddress, ProviderLanguage } from '../../../types';
import { apiAxiosV1 } from '../../../utils/service';
import {
  formatFormAddressesForApi,
  formatFormPhoneNumbersForApi,
  transformCodeToSelectOption,
  transformTypeToOption,
} from '../../utils';
import {
  CITIZENSHIP_OPTIONS,
  GENDER_OPTIONS,
  PHONE_TYPE_OPTIONS,
  VISA_STATUS_OPTIONS,
  VISA_TYPE_OPTIONS,
} from './constants';

import { AddressFormValues } from '~/features/types';
import { UpdateEvent } from './events';
import {
  ContactFormValues,
  Context,
  EmailFormValues,
  OptionStringType,
  PersonalFormValues,
  PhoneFormValues,
  ProfessionalInfoValues,
  SensitiveInfoValues,
} from './types';

/**
 * [MCV-1101] Extract a YYYY-MM-DD date string without going through `new Date()`.
 *
 * The previous implementation used `new Date(s).toISOString().split('T')[0]`,
 * which parses date-only strings as UTC midnight. For users in negative UTC
 * offsets (all of the Americas), any datetime input that falls in the late
 * evening locally crosses midnight in UTC and round-trips to the following
 * day. Result: DOB entered as May 15 came back as May 16 after a save/reload
 * cycle — so users perceived "my DOB didn't save".
 *
 * This helper preserves the user's calendar day by parsing the string
 * directly when it already looks like `YYYY-MM-DD`, and otherwise uses
 * *local* Date parts instead of UTC.
 */
export const toYMDString = (value: string | null | undefined): string => {
  if (!value) return '';
  const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (ymdMatch) {
    return `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** API request body shapes — models the PATCH payload, not the form values.
 *  Form option objects (e.g. { label, value }) are unwrapped to primitives before sending. */
type ProfessionalUpdateBody = {
  npiNum: string | null;
  providerCategoryId: number | undefined;
  providerTypeId: number | undefined;
  languages: ProviderLanguage[];
};

type ContactUpdateBody = {
  addresses: Address[];
  phoneNumbers: PhoneNumber[];
  emailAddresses: EmailFormValues[];
};

type PersonalUpdateBody = {
  firstName: string;
  lastName: string;
  middleName: string | null;
  prefixName: string;
  suffixName: string;
  details: {
    birthCountryCode: string;
    citizenshipStatus: string;
    birthState: string;
    birthCity: string;
    gender: string;
    dateOfBirth: string;
    visaStatus: string | null;
    visaType: string | null;
  };
};

type SensitiveUpdateBody = {
  details: {
    ssnEnc: string | null | undefined;
    driversLicenseNumEnc: string | null | undefined;
    driversLicenseState: string | null | undefined;
    passportNumberEnc: string | null | undefined;
  };
};

// [MCV-1905] An encrypted field the API could not decrypt reads back as empty, so the form
// shows it blank. Only a field that loaded with a value and was then emptied is a clear
// (`null`); one that loaded empty is left out of the PATCH so the stored ciphertext survives.
const sensitiveFieldForApi = (value: string | undefined, loaded: string | undefined) =>
  value || (loaded ? null : undefined);

type UpdateProviderBody =
  | ProfessionalUpdateBody
  | ContactUpdateBody
  | PersonalUpdateBody
  | SensitiveUpdateBody;

export function transformLangToOptions(
  langFromApi: ProviderLanguage[]
): { label: string; value: string; isFixed?: boolean }[] {
  const options: { label: string; value: string; isFixed?: boolean }[] = [];
  langFromApi.forEach((lang) => {
    const option = {
      value: lang.languageCode,
      label: lang.languageSpoken,
      isFixed: lang.languageCode === 'en', // If language is english, set isFixed to true
    };

    if (!options.includes(option)) {
      options.push(option);
    }
  });
  return options;
}

export const orderOptions = (options: readonly OptionStringType[]) => {
  return options
    .filter((option) => option.isFixed)
    .concat(options.filter((option) => !option.isFixed));
};

export function transformLanguagesForApi(
  languages: { value: string; label: string }[]
): ProviderLanguage[] {
  const transformedArray: ProviderLanguage[] = [];
  languages.forEach((lang) => {
    const apiLang = { languageCode: lang.value, languageSpoken: lang.label };
    if (!transformedArray.includes(apiLang)) {
      transformedArray.push(apiLang);
    }
  });
  return transformedArray;
}

export const normalizeNpiInput = (value: string) => value.replace(/\D/g, '').slice(0, 10);

// [MCV-1905] The PATCH is partial: a key that is absent from the JSON keeps its stored value,
// and `undefined` is dropped by JSON.stringify. A cleared optional field must therefore be an
// explicit `null`, which the API turns into a cleared column.
export const personalUpdateBodyFromValues = (_values: PersonalFormValues): PersonalUpdateBody => {
  const birthCountryCode = _values.birthCountryCode.value;
  return {
    firstName: _values.firstName,
    lastName: _values.lastName,
    middleName: _values.middleName || null,
    prefixName: _values.prefixName?.value || '',
    suffixName: _values.suffixName.trim(),
    details: {
      birthCountryCode,
      citizenshipStatus: _values.citizenshipStatus.value,
      birthState: birthCountryCode === 'US' ? _values.birthState.value : '',
      birthCity: _values.birthCity,
      gender: _values.gender.value,
      dateOfBirth: toYMDString(_values.dateOfBirth as string),
      visaStatus: _values.visaStatus?.value || null,
      visaType: _values.visaType?.value || null,
    },
  };
};

export const professionalUpdateBodyFromValues = (
  _values: ProfessionalInfoValues
): ProfessionalUpdateBody => ({
  npiNum: _values.npiNum || null,
  providerCategoryId: _values.providerCategory?.value,
  providerTypeId: _values.providerType?.value || undefined,
  languages: transformLanguagesForApi(_values.languages),
});

export const updateProviderAndDetails = async ({
  input,
}: {
  input: { context: Context; event: UpdateEvent };
}) => {
  const { context, event } = input;
  const { values } = event;
  const { id } = context.provider;

  let body: UpdateProviderBody;
  let _values:
    | ProfessionalInfoValues
    | ContactFormValues
    | PersonalFormValues
    | SensitiveInfoValues;

  switch (event.type) {
    case 'ON_SUBMIT_PROFESSIONAL':
      _values = values as ProfessionalInfoValues;
      body = professionalUpdateBodyFromValues(_values);
      break;
    case 'ON_SUBMIT_CONTACT':
      _values = values as ContactFormValues;
      body = {
        addresses: formatFormAddressesForApi(_values.addresses),
        phoneNumbers: formatFormPhoneNumbersForApi(_values.phoneNumbers),
        emailAddresses: _values.emailAddresses,
      };
      break;
    case 'ON_SUBMIT_PERSONAL':
      _values = values as PersonalFormValues;
      body = personalUpdateBodyFromValues(_values);
      break;
    case 'ON_SUBMIT_SENSITIVE': {
      _values = values as SensitiveInfoValues;
      // The baseline is fresh only because every successful save sends PROVIDER_UPDATED,
      // which reloads context.provider; setUpdateContext alone does not refresh it.
      const loaded = sensitiveInfoFromProvider(context.provider);
      body = {
        details: {
          ssnEnc: sensitiveFieldForApi(_values.ssnEnc, loaded.ssnEnc),
          driversLicenseNumEnc: sensitiveFieldForApi(
            _values.driversLicenseNumEnc,
            loaded.driversLicenseNumEnc
          ),
          driversLicenseState: sensitiveFieldForApi(
            _values.driversLicenseState?.value,
            loaded.driversLicenseState?.value
          ),
          passportNumberEnc: sensitiveFieldForApi(
            _values.passportNumberEnc,
            loaded.passportNumberEnc
          ),
        },
      };
      break;
    }
    default:
      throw new Error('Profile update form values must be sent with correct event type!');
  }

  // [MCV-1904] Goes through apiAxiosV1 (not bare axios) so the request carries the token, the
  // X-Acting-As-Provider delegation header, and the expired-token retry. Without the delegation
  // header every profile save by a delegated employer is rejected by the API.
  return apiAxiosV1.patch(`/providers/${id}`, body);
};

export const personalInfoFromProvider = (provider: Provider | undefined): PersonalFormValues => {
  return {
    firstName: provider?.firstName || '',
    lastName: provider?.lastName || '',
    middleName: provider?.middleName || '',
    prefixName: provider?.prefixName
      ? transformCodeToSelectOption(provider.prefixName, PREFIX_OPTIONS, PREFIX_OPTIONS[0])
      : PREFIX_OPTIONS[0],
    suffixName: provider?.suffixName || '',
    dateOfBirth: toYMDString(provider?.details?.dateOfBirth as string | undefined),
    gender: provider?.details?.gender
      ? transformCodeToSelectOption(provider.details.gender, GENDER_OPTIONS, GENDER_OPTIONS[0])
      : GENDER_OPTIONS[0],
    citizenshipStatus: provider?.details?.citizenshipStatus
      ? transformCodeToSelectOption(
          provider.details.citizenshipStatus,
          CITIZENSHIP_OPTIONS,
          CITIZENSHIP_OPTIONS[0]
        )
      : CITIZENSHIP_OPTIONS[0],
    // [MCV-1905] A stored visa the selects don't list (CV/packet parsing writes free text,
    // e.g. 'Applied For' or 'J-1') stays selected as itself, so a Personal save sends it
    // back unchanged instead of replacing it with 'None'.
    visaStatus: provider?.details?.visaStatus
      ? transformCodeToSelectOption(provider.details.visaStatus, VISA_STATUS_OPTIONS, {
          label: provider.details.visaStatus,
          value: provider.details.visaStatus,
        })
      : VISA_STATUS_OPTIONS[0],
    visaType: provider?.details?.visaType
      ? transformCodeToSelectOption(provider.details.visaType, VISA_TYPE_OPTIONS, {
          label: provider.details.visaType,
          value: provider.details.visaType,
        })
      : VISA_TYPE_OPTIONS[0],
    birthCountryCode: provider?.details?.birthCountryCode
      ? transformCodeToSelectOption(
          provider.details.birthCountryCode,
          COUNTRIES,
          COUNTRIES.find((c) => c.value === 'US') || COUNTRIES[0]
        )
      : COUNTRIES[0],
    birthState: provider?.details?.birthState
      ? transformCodeToSelectOption(provider.details.birthState, STATES, {
          label: `${provider.details.birthState} (re-select)`,
          value: provider.details.birthState,
        })
      : { label: '', value: '' },
    birthCity: provider?.details?.birthCity || '',
  };
};

export const professionalInfoFromProvider = (
  provider: Provider | undefined
): ProfessionalInfoValues => {
  return {
    providerTypeId: provider?.type?.id as number,
    providerCategoryId: provider?.category.id as number,
    npiNum: provider?.npiNum || '',
    providerCategory: {
      label: provider?.category.providerCategoryCode as string,
      value: provider?.category.id as number,
    },
    providerType: provider?.type
      ? {
          label: provider?.type.providerTypeCode,
          value: provider?.type.id,
        }
      : null,
    languages: provider?.languages?.length
      ? transformLangToOptions(provider.languages)
      : [{ value: 'en', label: 'English', isFixed: true }],
  };
};
export const sensitiveInfoFromProvider = (provider: Provider | undefined): SensitiveInfoValues => {
  return {
    ssnEnc: (provider?.details?.ssnEnc as string) || '',
    driversLicenseNumEnc: (provider?.details?.driversLicenseNumEnc as string) || '',
    driversLicenseState: transformCodeToSelectOption(
      provider?.details?.driversLicenseState as string,
      STATES,
      { value: '', label: '' }
    ),
    passportNumberEnc: (provider?.details?.passportNumberEnc as string) || '',
  };
};

export const emailAddressesProvider = (provider: Provider | undefined): EmailFormValues[] => {
  return provider?.emailAddresses || [];
};

export const addressesProvider = (provider: Provider | undefined): AddressFormValues[] => {
  const transformedAddresses: AddressFormValues[] = [];

  if (provider?.addresses) {
    provider.addresses.forEach((address: ProviderAddress) => {
      transformedAddresses.push({
        ...address,
        addressType: address.addressType
          ? transformTypeToOption(address.addressType)
          : { label: 'Permanent', value: 'Permanent' },
        stateCode: transformCodeToSelectOption(address.stateCode, STATES, {
          value: '',
          label: '',
        }),
        countryCode: transformCodeToSelectOption(
          address.countryCode,
          LIMITED_COUNTRY_OPTIONS,
          LIMITED_COUNTRY_OPTIONS[0]
        ),
      });
    });
  }
  return transformedAddresses;
};

export const phoneNumbersProvider = (provider: Provider | undefined): PhoneFormValues[] => {
  const transformedNumbers: PhoneFormValues[] = [];

  if (provider?.phoneNumbers) {
    provider.phoneNumbers.forEach((numObj: PhoneNumber) => {
      transformedNumbers.push({
        ...numObj,
        phoneType: transformCodeToSelectOption(
          numObj.phoneType,
          PHONE_TYPE_OPTIONS,
          PHONE_TYPE_OPTIONS[0]
        ),
        countryCode: transformCodeToSelectOption(
          numObj.countryCode,
          LIMITED_PHONE_COUNTRY_CODE,
          LIMITED_PHONE_COUNTRY_CODE[0]
        ),
      });
    });
  }

  return transformedNumbers;
};
